// Minimal code sample generation utilities inspired by ui-component-lab artefact

function sampleFromSchema(schema) {
  if (!schema) return null;
  if (schema.example !== undefined) return schema.example;
  if (schema.$ref) return `<${schema.$ref.split('/').pop()}>`;
  const t = schema.type;
  if (t === 'string') return 'string';
  if (t === 'number' || t === 'integer') return 0;
  if (t === 'boolean') return false;
  if (t === 'array') return [sampleFromSchema(schema.items) ?? 'item'];
  if (t === 'object' || schema.properties) {
    const out = {};
    Object.entries(schema.properties || {}).forEach(([k, v]) => {
      out[k] = sampleFromSchema(v);
    });
    return out;
  }
  return null;
}

function buildUrlBase(swagger, path, pathParams = []) {
  // OpenAPI 3
  if (swagger?.servers?.length) {
    let base = swagger.servers[0].url || '';
    pathParams.forEach((p) => {
      base = base.replace(`{${p.name}}`, `:${p.name}`);
    });
    return `${base}${path}`;
  }
  // Swagger 2.0
  const scheme = swagger?.schemes?.[0] || 'https';
  const host = swagger?.host || '';
  const basePath = swagger?.basePath || '';
  return `${scheme}://${host}${basePath}${path}`;
}

function buildUrlWithQuery(swagger, path, pathParams = [], queryParams = []) {
  let url = buildUrlBase(swagger, path, pathParams);
  // Replace path params
  pathParams.forEach((p) => {
    url = url.replace(`{${p.name}}`, `<${p.name}>`);
  });
  const q = (queryParams || [])
    .map((q) => `${encodeURIComponent(q.name)}=<${q.name}>`)
    .join('&');
  return q ? `${url}?${q}` : url;
}

export function generateCurlSnippet({
  swagger,
  method,
  path,
  parameters = [],
  requestBody,
}) {
  const pathParams = parameters.filter((p) => p.in === 'path');
  const queryParams = parameters.filter((p) => p.in === 'query');
  const headerParams = parameters.filter((p) => p.in === 'header');
  const contentType = requestBody?.content
    ? Object.keys(requestBody.content)[0]
    : undefined;

  const url = buildUrlWithQuery(swagger, path, pathParams, queryParams);
  const lines = [`curl -X ${String(method || '').toUpperCase()} "${url}"`];

  if (contentType && !/^multipart\/form-data/.test(contentType)) {
    lines.push(`  -H "Content-Type: ${contentType}"`);
  }
  headerParams.forEach((h) => {
    lines.push(`  -H "${h.name}: <${h.name}>"`);
  });

  // Body
  if (requestBody?.content) {
    if (/^multipart\/form-data/.test(contentType)) {
      lines.push('  -F "file=@/path/to/file.bin"');
    } else {
      const example = sampleFromSchema(requestBody.content[contentType]?.schema);
      const body = example ? JSON.stringify(example, null, 2) : '{}';
      lines.push(`  --data '${body.replace(/'/g, "'\\''")}'`);
    }
  }

  return lines.join(' \\\n');
}

export function generateFetchSnippet({
  swagger,
  method,
  path,
  parameters = [],
  requestBody,
}) {
  const pathParams = parameters.filter((p) => p.in === 'path');
  const queryParams = parameters.filter((p) => p.in === 'query');
  const headerParams = parameters.filter((p) => p.in === 'header');
  const contentType = requestBody?.content
    ? Object.keys(requestBody.content)[0]
    : undefined;

  const url = buildUrlWithQuery(swagger, path, pathParams, queryParams);
  const headers = {};
  if (contentType && !/^multipart\/form-data/.test(contentType)) {
    headers['Content-Type'] = contentType;
  }
  headerParams.forEach((h) => {
    if (!/^authorization$/i.test(h.name)) headers[h.name] = `<${h.name}>`;
  });

  const opts = { method: String(method || '').toUpperCase(), headers };
  if (requestBody?.content && !/^multipart\/form-data/.test(contentType)) {
    const example = sampleFromSchema(requestBody.content[contentType]?.schema);
    opts.body = JSON.stringify(example ?? {}, null, 2);
  }
  return `fetch("${url}", ${JSON.stringify(opts, null, 2)})\n  .then(r => r.json())\n  .then(console.log)\n  .catch(console.error)`;
}

export default {
  generateCurlSnippet,
  generateFetchSnippet,
};

